/**
 * Utility functions for SprintRay Order Form
 */

import type { Currency } from './types';

// Get the plugin URL from WordPress localized data
declare global {
	interface Window {
		sprintRayFormSettings?: {
			pluginUrl?: string;
			translationOverrides?: Record< string, string >; // Site/event text overrides (flat dot-path map)
			initialProducts?: any[];
			initialPromotions?: any[];
			initialPartners?: any[]; // Trade partners from master-dealer-hub
			isPartnerSelectionRestricted?: boolean; // True only when the event has an explicit, non-empty dealer restriction
			initialCountries?: { code: string; label: string }[]; // All partner-locations terms (master-dealer-hub), unfiltered by event
			eventTitle?: string;
			promoValidity?: string;
			promotionFreeResinOptions?: any[]; // Free resin options from active promotions
			currency?: Currency; // Event currency
			vatDisplayEnabled?: boolean; // VAT display enabled flag
			vatRate?: number; // VAT rate percentage
		};
	}
}

export const getPluginUrl = (): string => {
	// The frontend order form localizes sprintRayFormSettings; the WP-admin
	// print page (a separate bundle/window global, see admin-print.tsx)
	// localizes sprintrayPrintOrder instead — check both.
	return (
		window.sprintRayFormSettings?.pluginUrl ||
		window.sprintrayPrintOrder?.pluginUrl ||
		'/wp-content/plugins/sre000-order-plugin/dist/'
	);
};

/**
 * Generate a stable client-side order id. Set once per order and reused on
 * every retry (including offline-queue resubmission), so the server can
 * recognize a retried submission and avoid creating a duplicate order.
 */
export const generateClientOrderId = (): string => {
	if ( typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function' ) {
		return crypto.randomUUID();
	}
	return `order-${ Date.now() }-${ Math.random().toString( 36 ).substr( 2, 9 ) }`;
};

export const getImageUrl = (imagePath: string): string => {
	// If it's already a full URL, return as-is
	if (!imagePath) return '';
	if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
		return imagePath;
	}

	// Otherwise, prepend the plugin URL
	const pluginUrl = getPluginUrl();
	return `${pluginUrl}${imagePath}`;
};

/**
 * Currency format configuration
 */
export interface CurrencyFormat {
	symbol: string;
	locale: string;
	position: 'before' | 'after';
}

const CURRENCY_FORMATS: Record<Currency, CurrencyFormat> = {
	EUR: { symbol: '€', locale: 'de-DE', position: 'after' },
	GBP: { symbol: '£', locale: 'en-GB', position: 'before' },
	USD: { symbol: '$', locale: 'en-US', position: 'before' },
	EUR_EE: { symbol: '€', locale: 'pl-PL', position: 'after' },
};

/**
 * Format a price amount according to the currency
 *
 * @param amount - The price amount to format
 * @param currency - The currency code (default: 'EUR')
 * @returns Formatted price string with currency symbol
 *
 * @example
 * formatPrice(1299.00, 'EUR') // "1.299,00€"
 * formatPrice(1290.00, 'GBP') // "£1,290.00"
 * formatPrice(1500.00, 'USD') // "$1,500.00"
 */
export const formatPrice = (amount: number, currency: Currency = 'EUR'): string => {
	const format = CURRENCY_FORMATS[currency];
	const formatted = amount.toLocaleString(format.locale, {
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
	return format.position === 'before'
		? `${format.symbol}${formatted}`
		: `${formatted}${format.symbol}`;
};

/**
 * Get the currency symbol for a given currency code
 *
 * @param currency - The currency code
 * @returns The currency symbol
 */
export const getCurrencySymbol = (currency: Currency = 'EUR'): string => {
	return CURRENCY_FORMATS[currency]?.symbol || '€';
};

/**
 * Get the event currency from window settings or default to EUR
 *
 * @returns The event currency code
 */
export const getEventCurrency = (): Currency => {
	return window.sprintRayFormSettings?.currency || 'EUR';
};

/**
 * Get VAT display setting from window
 *
 * @returns True if VAT display is enabled
 */
export const getVatDisplayEnabled = (): boolean => {
	return window.sprintRayFormSettings?.vatDisplayEnabled || false;
};

/**
 * Get VAT rate from window
 *
 * @returns VAT rate percentage (default 20.0)
 */
export const getVatRate = (): number => {
	return window.sprintRayFormSettings?.vatRate || 20.0;
};

/**
 * Calculate gross price from net price (add VAT)
 * @param netPrice - Price excluding VAT
 * @param vatRate - VAT rate in percentage
 * @returns Price including VAT
 */
export const calculateGrossPrice = (netPrice: number, vatRate: number): number => {
	return netPrice * (1 + vatRate / 100);
};

/**
 * Calculate VAT amount from net price
 * @param netPrice - Price excluding VAT
 * @param vatRate - VAT rate in percentage
 * @returns VAT amount
 */
export const calculateVatAmount = (netPrice: number, vatRate: number): number => {
	return netPrice * (vatRate / 100);
};
